import React, { useCallback, useRef, useState } from "react";
import { Camera, Upload, ImagePlus, X, Loader2, Video, StopCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { uploadPhoto, photoSrc } from "@/lib/uploads";
import { formatApiError } from "@/lib/api";
import { isNative } from "@/lib/platform";
import { captureNativePhoto, pickNativePhoto } from "@/lib/native-camera";

/**
 * Reusable drag/drop + camera-capture uploader.
 * Props:
 *   value            — string | string[]         current photo URL(s)
 *   onChange         — (val) => void             called with new string or array
 *   purpose          — "device" | "claim" | ...  passed to /api/files/upload
 *   multiple         — boolean                   allow multi (max)
 *   max              — number                    max photos when multiple (default 4)
 *   testIdPrefix     — string                    for e2e selectors
 */
export default function PhotoUploader({
  value,
  onChange,
  purpose = "device",
  multiple = false,
  max = 4,
  testIdPrefix = "photo",
}) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const photos = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const setPhotos = (next) => {
    if (multiple) onChange(next);
    else onChange(next[0] || "");
  };

  const uploadFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const remaining = multiple ? Math.max(0, max - photos.length) : 1;
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) {
      toast.error(`Maximum ${max} photos`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) {
        if (!f.type.startsWith("image/")) {
          toast.error(`${f.name}: only images allowed`);
          continue;
        }
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name}: max 10 MB`);
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          const r = await uploadPhoto(f, purpose);
          uploaded.push(r.url);
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || `Upload failed: ${f.name}`);
        }
      }
      if (uploaded.length > 0) {
        setPhotos(multiple ? [...photos, ...uploaded] : [uploaded[uploaded.length - 1]]);
        toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded`);
      }
    } finally {
      setUploading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, multiple, max, purpose]);

  // --- Drag & drop ---
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer?.files);
  };

  // --- Live camera (desktop) / native camera (iOS/Android) ---
  const openCamera = async () => {
    // Native platforms → use OS camera picker directly
    if (isNative()) {
      const f = await captureNativePhoto(purpose);
      if (f) await uploadFiles([f]);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 50);
    } catch (err) {
      if (mobileInputRef.current) mobileInputRef.current.click();
    }
  };

  const openLibrary = async () => {
    if (isNative()) {
      const f = await pickNativePhoto(purpose);
      if (f) await uploadFiles([f]);
      return;
    }
    inputRef.current?.click();
  };
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  };
  const takeSnapshot = async () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 480;
    canvas.getContext("2d").drawImage(v, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      stopCamera();
      await uploadFiles([file]);
    }, "image/jpeg", 0.9);
  };

  const removeAt = (idx) => {
    setPhotos(photos.filter((_, i) => i !== idx));
  };

  const showSlots = multiple || photos.length === 0;
  const canAddMore = multiple ? photos.length < max : photos.length === 0;

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-uploader`}>
      {/* Preview grid */}
      {photos.length > 0 && (
        <div className={`grid ${multiple ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-1"} gap-2`}>
          {photos.map((p, i) => (
            <div
              key={`${p.slice(0, 32)}-${i}`}
              data-testid={`${testIdPrefix}-preview-${i}`}
              className="relative aspect-square rounded-xl overflow-hidden border border-border group"
            >
              <img src={photoSrc(p, "thumb")} alt={`upload ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`${testIdPrefix}-remove-${i}`}
                className="absolute top-1.5 right-1.5 bg-background/85 backdrop-blur rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      {showSlots && canAddMore && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testIdPrefix}-dropzone`}
          className={`rounded-xl border-2 border-dashed transition-all cursor-pointer p-6 text-center flex flex-col items-center gap-2 ${
            dragOver
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/60 hover:bg-muted/40"
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <div className="text-sm font-medium">Uploading…</div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <ImagePlus className="w-5 h-5 text-primary" />
              </div>
              <div className="text-sm font-medium">
                {dragOver ? "Drop to upload" : "Drag photos here"}
              </div>
              <div className="text-xs text-muted-foreground">
                or click to browse · up to {multiple ? `${max} photos` : "1 photo"} · 10 MB max
              </div>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple={multiple}
            onChange={(e) => uploadFiles(e.target.files)}
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
          />
          <input
            ref={mobileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => uploadFiles(e.target.files)}
            className="hidden"
            data-testid={`${testIdPrefix}-camera-input`}
          />
        </div>
      )}

      {/* Action buttons */}
      {canAddMore && !cameraOpen && (
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={openLibrary}
            disabled={uploading}
            data-testid={`${testIdPrefix}-browse-btn`}
          >
            <Upload className="w-3.5 h-3.5 mr-2" /> {isNative() ? "Photo library" : "Choose file"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={openCamera}
            disabled={uploading}
            data-testid={`${testIdPrefix}-camera-btn`}
          >
            <Camera className="w-3.5 h-3.5 mr-2" /> Take photo
          </Button>
        </div>
      )}

      {/* Live camera modal (desktop-friendly, also works on mobile browsers that grant camera) */}
      {cameraOpen && (
        <div className="rounded-xl overflow-hidden border border-border bg-black" data-testid={`${testIdPrefix}-camera-live`}>
          <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-96 object-contain bg-black" />
          <canvas ref={canvasRef} className="hidden" />
          <div className="flex justify-between items-center bg-card p-2 border-t border-border">
            <Button type="button" size="sm" variant="ghost" onClick={stopCamera} data-testid={`${testIdPrefix}-camera-cancel`}>
              <StopCircle className="w-4 h-4 mr-2" /> Cancel
            </Button>
            <Button type="button" size="sm" onClick={takeSnapshot} data-testid={`${testIdPrefix}-camera-capture`}>
              <Video className="w-4 h-4 mr-2" /> Capture
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
